import React from "react";
import t, { changeLanguage } from "../../components/I18N/TranslateHelpers";
import { GetCurrentLocale, pathname, translateAppFormSubLabel } from "../../components/Utils";
import { useSelector } from "react-redux";
import { RootState, store } from "app/store";
import { SpinnerOnTop } from "components/SpinnerOnTop";
import { PlacementSchoolDateStatus, PlacementSchoolStatus, PlacementType } from "features/Entity/PlacementInfo";
import { downloadPlacementInfoDocument, getPlacementApplicationFormFile } from "features/Slice/PlacementInfoSlice";
import PageTitle from "./PageTitle";


const PlacementInfo: React.FC = () => {
    const locale = GetCurrentLocale();
    changeLanguage(locale);

    const placementInfo = useSelector((state: RootState) => state.placementInfoRdc.placementInfo);
    const isRequesting = useSelector((state: RootState) => state.placementInfoRdc.isRequesting);
    const currentAppForm = useSelector((state: RootState) => state.applicationFormRdc.applicationForm);
    const userInfo = useSelector((state: RootState) => state.userRdc.userInfo);


      const getEnumLabel = <T extends Record<string, string | number>>(enumObj: T, value: number): string => {
        if (value === -1) return t('unknown');
        
        const label = Object.keys(enumObj).find(key => enumObj[key as keyof T] === value);
        return label ? t(label) : t('unknown');
      };
      
      const handleDownloadClick = (placementDocumentUid: string, fileName: string) => {
        store.dispatch(downloadPlacementInfoDocument(placementDocumentUid, fileName));
      };
      const handleGetPlacementApplicationFormFile = (placementDocumentUid: string, fileName: string|undefined) => {
        if(fileName!== undefined){
            store.dispatch(getPlacementApplicationFormFile(placementDocumentUid, fileName));
        }
      };

      const yesNoBadge = (value: boolean | undefined) => {
        if (value === undefined || value === null) {
            return null;
        }
        return value ? (
            <span className="badge badge-light-success">{t('yes')}</span>
        ) : (
            <span className="badge badge-light-danger">{t('no')}</span>
        );
    };
    
    const renderField = (label: string, value: any) => {
        if (!value) {
            return null;
        }
        return (
            <p className="d-flex align-items-start">
                <strong>{t(label)} :</strong> {value}
            </p>
        );
    };
    
    return (
        <>
        	<PageTitle title={t("placement_info")} />
            <div className="card">
                <div className="card-header">
                    <div className="d-sm-flex justify-content-between align-items-start flex-column">
                        <h6 className="mb-0">
                            {currentAppForm ? `${currentAppForm.labelComplete}` : ""}
                        </h6>
                        <small>{currentAppForm ? translateAppFormSubLabel(currentAppForm, currentAppForm.isStaffForAppForm, currentAppForm.isSendingPartnerForAppForm) : ""}</small>
                    </div>
                </div>
            </div>
            {
                isRequesting ? <SpinnerOnTop />
                    :
                    placementInfo ?
                        <div className="row">

                            <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12 pb-3 same-height-as-parent">
                                <div className="card">
                                    <div className="card-header faq-header pb-0">
                                        <h5 className="ps-0 d-flex">{t('global_info')}</h5>
                                    </div>
                                    <div className="card-body placement-body">
                                        {
                                            placementInfo.hfAppFormFile ?
                                                <p>
                                                <strong>{t('hfAppFormFile')} : </strong>
                                                <a
                                                    href="#"
                                                    target="_blank"
                                                    rel="noopener noreferrer"            
                                                    onClick={(e) => {
                                                        e.preventDefault(); 
                                                        handleGetPlacementApplicationFormFile(placementInfo.uid, placementInfo.hfAppFormFile);
                                                    }}
                                                >
                                                    {placementInfo.hfAppFormFile}
                                                </a>
                                                </p>
                                            : null
                                        }
                                        {renderField("hfAppFormComment", placementInfo.hfAppFormComment)}
                                        {renderField("placementType", getEnumLabel(PlacementType, placementInfo.placementType))}
                                        {renderField("isSingleParent", yesNoBadge(placementInfo.isSingleParent))}
                                        {renderField("isDoublePlacement", yesNoBadge(placementInfo.isDoublePlacement))}
                                        {renderField("isSharingBedroom", yesNoBadge(placementInfo.isSharingBedroom))}
                                        
                                        {placementInfo.isSharingBedroom && placementInfo.sharingBedroomDetails && (
                                            <div dangerouslySetInnerHTML={{ __html: placementInfo.sharingBedroomDetails }}></div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12 pb-3 same-height-as-parent">
                                <div className="card">
                                    <div className="card-header faq-header pb-0">
                                        <h5 className="ps-0 d-flex">{t('host_family')}</h5>
                                    </div>
                                    <div className="card-body placement-body">
                                    {placementInfo.hfName ? (
                                        <p>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-users">
                                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                                <circle cx="9" cy="7" r="4"></circle>
                                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                            </svg>
                                            {placementInfo.hfName}
                                        </p>
                                    ) : null}

                                    {placementInfo.hfPhone ? (
                                        <p>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-phone-call">
                                                <path d="M15.05 5A5 5 0 0 1 19 8.95M15.05 1A9 9 0 0 1 23 8.94m-1 7.98v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                            </svg>
                                            {placementInfo.hfPhone}
                                        </p>
                                    ) : null}

                                    {placementInfo.hfEmail ? (
                                        <p>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-mail">
                                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                                <polyline points="22,6 12,13 2,6"></polyline>
                                            </svg>
                                            {placementInfo.hfEmail}
                                        </p>
                                    ) : null}

                                    {placementInfo.hfStreet || placementInfo.hfZip ? (
                                        <p className="mb-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-home">
                                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                                <polyline points="9 22 9 12 15 12 15 22"></polyline>
                                            </svg>
                                            {placementInfo.hfStreet} {placementInfo.hfZip}
                                        </p>
                                    ) : null}
                                    {placementInfo.hfCity || placementInfo.hfState ? (
                                        <p>
                                            <strong className="pl-25">{placementInfo.hfCity} {placementInfo.hfState}</strong>
                                        </p>
                                    ) : null}

                                    </div>
                                </div>
                            </div>
                            <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12 pb-3 same-height-as-parent">
                                <div className="card">
                                    <div className="card-header faq-header pb-0">
                                        <h5 className="ps-0 d-flex">{t('school')}</h5>
                                    </div>
                                    <div className="card-body placement-body">
                                        {renderField("schoolName", placementInfo.schoolName)}
                                        {renderField("schoolStatus", getEnumLabel(PlacementSchoolStatus, placementInfo.schoolStatus ?? -1))}
                                        {renderField("schoolDateStatus", getEnumLabel(PlacementSchoolDateStatus, placementInfo.schoolDateStatus ?? -1))}
                                    </div>
                                </div>
                            </div>
                            <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12 pb-3 same-height-as-parent">
                                <div className="card">
                                    <div className="card-header faq-header pb-0">
                                        <h5 className="ps-0 d-flex">{t('family')}</h5>
                                    </div>
                                    <div className="card-body placement-body">
                                    {placementInfo.parent1FirstName ? (
                                    <>
                                        <h6>{t('parent')} 1</h6>
                                        <p>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-users">
                                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                                <circle cx="9" cy="7" r="4"></circle>
                                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                            </svg>
                                            {placementInfo.parent1FirstName} ({placementInfo.parent1Age} {t('years')})
                                        </p>
                                    </>
                                ) : null}

                                {placementInfo.parent1Occupation ? (
                                    <p>
                                        <i className="icon-briefcase"></i> {placementInfo.parent1Occupation}
                                    </p>
                                ) : null}

                                {placementInfo.parent1Phone ? (
                                    <p>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-phone-call">
                                            <path d="M15.05 5A5 5 0 0 1 19 8.95M15.05 1A9 9 0 0 1 23 8.94m-1 7.98v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                        </svg>
                                        {placementInfo.parent1Phone}
                                    </p>
                                ) : null}
                                <hr />
                                {placementInfo.parent2FirstName ? (
                                    <>
                                        <h6>{t('parent')} 2</h6>
                                        <p>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-users">
                                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                                <circle cx="9" cy="7" r="4"></circle>
                                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                            </svg>
                                            {placementInfo.parent2FirstName} ({placementInfo.parent2Age} {t('years')})
                                        </p>
                                    </>
                                ) : null}

                                {placementInfo.parent2Occupation ? (
                                    <p>
                                        <i className="icon-briefcase"></i> {placementInfo.parent2Occupation}
                                    </p>
                                ) : null}

                                {placementInfo.parent2Phone ? 
                                    <>
                                         <p>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-phone-call">
                                                <path d="M15.05 5A5 5 0 0 1 19 8.95M15.05 1A9 9 0 0 1 23 8.94m-1 7.98v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                            </svg>
                                            {placementInfo.parent2Phone}
                                        </p>
                                    </>
                                 : null}
                               <hr />
                                    {placementInfo.childrenDetails && (
                                        <>
                                        <p className="d-flex align-items-start flex-column">
                                            <strong>{t('childrenDetails')} :</strong>
                                            <div dangerouslySetInnerHTML={{ __html: placementInfo.childrenDetails }}></div>
                                        </p>
                                        <hr/>
                                        </>
                                    )}
                                        {
                                            renderField("hasPets", yesNoBadge(placementInfo.hasPets))
                                        }
                                        {placementInfo.hasPets && placementInfo.petsDetails && (
                                            <div dangerouslySetInnerHTML={{ __html: placementInfo.petsDetails }}></div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12 pb-3 same-height-as-parent">
                                <div className="card">
                                    <div className="card-header faq-header pb-0">
                                        <h5 className="ps-0 d-flex">{t('additional_details')}</h5>
                                    </div>
                                    <div className="card-body additionnal-details-body">
                                    {renderField("applicantRegionalContact", placementInfo.applicantRegionalContact)}
                                    {renderField("applicantLocalContact", placementInfo.applicantLocalContact)}
                                            {placementInfo.hobbiesDetails && (
                                                <p className="d-flex align-items-start flex-column"><strong>{t('hobbiesDetails')} :</strong> <div dangerouslySetInnerHTML={{ __html: placementInfo.hobbiesDetails }}></div></p>
                                            )}
                                        { placementInfo.displayComment && renderField("displayComment", <div dangerouslySetInnerHTML={{ __html: placementInfo.displayComment }}></div>)}
                                        {renderField("specificImmunisation", placementInfo.specificImmunisation)}
                                        {placementInfo.specificImmunisationDetails && (
                                            <p className="d-flex align-items-start flex-column"><strong>{t('specificImmunisationDetails')} :</strong> <div dangerouslySetInnerHTML={{ __html: placementInfo.specificImmunisationDetails }}></div></p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12 pb-3 same-height-as-parent">
                                <div className="card">
                                    <div className="card-header faq-header pb-0">
                                        <h5 className="ps-0 d-flex">{t('documents')}</h5>
                                    </div>
                                    <div className="card-body placement-body">
                                        {placementInfo.placementDocuments.length > 0 ? (
                                            <ul className="ul-style-none">
                                                {placementInfo.placementDocuments.map((doc, index) => (
                                                    <li key={index} className="cursor-pointer list-group-item py-3 flex-wrap d-flex g-0 align-items-center justify-content-between">
                                                            {doc.name}
                                                            <a
                                                            href="#"
                                                            target="_blank"
                                                            rel="noopener noreferrer"            
                                                            onClick={(e) => {
                                                                e.preventDefault(); 
                                                                handleDownloadClick(doc.uid, doc.file);
                                                              }}
                                                        >
                                                            <i className="icofont icofont-download-alt mr-2"></i>
                                                        </a>
                                                    </li>
                                                ))}
                                                  {
                                            placementInfo.hfAppFormFile ?
                                                <li className="cursor-pointer list-group-item py-3 flex-wrap d-flex g-0 align-items-center justify-content-between">
                                                     {t('hfAppFormFile')}
                                                <a
                                                    href="#"
                                                    target="_blank"
                                                    rel="noopener noreferrer"            
                                                    onClick={(e) => {
                                                        e.preventDefault(); 
                                                        handleGetPlacementApplicationFormFile(placementInfo.uid, placementInfo.hfAppFormFile);
                                                    }}
                                                ><i className="icofont icofont-download-alt mr-2"></i>
                                                 </a>
                                                </li>
                                            : null
                                        }
                                            </ul>
                                        ) : (
                                            <p>{t('no_documents_available')}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        : null
            }
        </>
    );
};

export default PlacementInfo;
